/**
 * @flowstate/ai — Provider adapters
 *
 * Thin wrappers around TanStack AI provider adapters.
 * Centralises model selection logic so both apps resolve adapters the same way.
 */

import {
  DEFAULT_GEMINI_FLASH_MODEL,
  DEFAULT_GEMINI_PRO_MODEL,
  resolveGeminiModelId,
} from "@flowstate/types";
import type { AIConfig, ModelTier, AIProvider } from "../types/index.js";
import { EventType } from "@tanstack/ai";
import type { AnyTextAdapter, StreamChunk, TextOptions } from "@tanstack/ai";
import type { StructuredOutputOptions, StructuredOutputResult } from "@tanstack/ai/adapters";
import type { GoogleGenAIOptions } from "@google/genai";

/**
 * Attempts to repair truncated or incomplete JSON strings from LLM outputs.
 * Mirrors the edge-function helper in supabase/functions/_shared/ai/gemini-client.ts
 * to keep client-side parsing resilient.
 */
function repairTruncatedJson(jsonStr: string): string {
  let str = jsonStr.trim();

  try {
    JSON.parse(str);
    return str;
  } catch (_) {
    // Continue repair steps
  }

  let inString = false;
  let isEscaped = false;
  for (let i = 0; i < str.length; i++) {
    const char = str[i];
    if (char === "\\" && !isEscaped) {
      isEscaped = true;
    } else {
      if (char === '"' && !isEscaped) {
        inString = !inString;
      }
      isEscaped = false;
    }
  }
  if (inString) {
    str += '"';
  }

  const stack: string[] = [];
  inString = false;
  isEscaped = false;

  for (let i = 0; i < str.length; i++) {
    const char = str[i];
    if (char === "\\" && !isEscaped) {
      isEscaped = true;
    } else {
      if (char === '"' && !isEscaped) {
        inString = !inString;
      } else if (!inString) {
        if (char === "{" || char === "[") {
          stack.push(char);
        } else if (char === "}") {
          if (stack.length > 0 && stack[stack.length - 1] === "{") stack.pop();
        } else if (char === "]") {
          if (stack.length > 0 && stack[stack.length - 1] === "[") stack.pop();
        }
      }
      isEscaped = false;
    }
  }

  str = str.replace(/,\s*([}\]])/g, "$1").replace(/,\s*$/, "");

  while (stack.length > 0) {
    const open = stack.pop();
    str += open === "{" ? "}" : "]";
  }

  return str;
}


// ─── Default model maps ───────────────────────────────────────────────────────

const GEMINI_TIER_MODELS: Record<ModelTier, string> = {
  flash: DEFAULT_GEMINI_FLASH_MODEL,
  pro: DEFAULT_GEMINI_PRO_MODEL,
  ultra: DEFAULT_GEMINI_PRO_MODEL, // no distinct "ultra" yet; fall back to pro
};

const OPENAI_MODELS: Record<ModelTier, string> = {
  flash: "gpt-4o-mini",
  pro: "gpt-4o",
  ultra: "gpt-4o",
};

const ANTHROPIC_MODELS: Record<ModelTier, string> = {
  flash: "claude-3-5-haiku-latest",
  pro: "claude-3-5-sonnet-latest",
  ultra: "claude-3-opus-latest",
};

/**
 * OpenRouter — federated gateway exposing 300+ models behind one API.
 * We pick conservative cross-provider defaults; callers can override via
 * `config.model` with any OpenRouter model slug (e.g. "x-ai/grok-4",
 * "meta-llama/llama-4-maverick").
 */
const OPENROUTER_MODELS: Record<ModelTier, string> = {
  flash: "openai/gpt-5-mini",
  pro: "anthropic/claude-sonnet-4.5",
  ultra: "anthropic/claude-opus-4.5",
};

/**
 * Curated set surfaced in the client-side model picker.
 * Anything in OpenRouter's catalogue works at runtime — this is just the
 * shortlist we render by default. Update freely.
 *
 * Canonical definition lives in `@flowstate/types` so the edge brain can
 * build the same catalog without pulling in the client adapter package.
 */
export { OPENROUTER_PRESETS, type OpenRouterPresetId } from "@flowstate/types";

function resolveModel(provider: AIProvider, config: AIConfig): string {
  if (config.model) {
    return provider === "gemini"
      ? resolveGeminiModelId(config.model)
      : config.model;
  }
  const tier = config.tier ?? "flash";
  switch (provider) {
    case "openai":
      return OPENAI_MODELS[tier];
    case "anthropic":
      return ANTHROPIC_MODELS[tier];
    case "openrouter":
      return OPENROUTER_MODELS[tier];
    case "gemini":
    default:
      return GEMINI_TIER_MODELS[tier];
  }
}

/** Default OpenRouter flash-tier model — shared with ModelPicker. */
export const DEFAULT_OPENROUTER_FLASH_MODEL = OPENROUTER_MODELS.flash;

/**
 * Create a Gemini-compatible adapter with Bearer token support for OAuth.
 * Uses direct fetch when access tokens are provided (OAuth), otherwise uses SDK.
 */
class GeminiOAuthAdapter implements AnyTextAdapter {
  readonly kind = 'text' as const;
  readonly name = 'gemini' as const;
  readonly model: string;

  /**
   * @internal Type-only properties for inference.
   * `toolCapabilities` stays empty here — runtime tools are passed via
   * `TextOptions.tools` from the chat hook, which sources them from the
   * federated `@flowstate/tools` registry (filtered to `clientSafe: true`).
   */
  '~types': {
    providerOptions: Record<string, unknown>;
    inputModalities: readonly ['text'];
    messageMetadataByModality: Record<string, unknown>;
    toolCapabilities: Record<string, unknown>;
    toolCallMetadata: Record<string, unknown>;
    systemPromptMetadata: Record<string, unknown>;
  };

  private accessToken: string;
  private baseUrl = 'https://generativelanguage.googleapis.com/v1beta/models';

  constructor(model: string, accessToken: string) {
    this.model = model;
    this.accessToken = accessToken;
    this['~types'] = {
      providerOptions: {},
      inputModalities: ['text'] as const,
      messageMetadataByModality: {},
      toolCapabilities: {},
      toolCallMetadata: {},
      systemPromptMetadata: {},
    };
  }

  private buildHeaders(): Record<string, string> {
    const projectId =
      process.env.GOOGLE_CLOUD_PROJECT ??
      process.env.GCP_PROJECT_ID ??
      process.env.GOOGLE_PROJECT_ID;
    const headers: Record<string, string> = {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${this.accessToken}`,
    };
    if (projectId) {
      headers['x-goog-user-project'] = projectId;
    } else {
      // eslint-disable-next-line no-console
      console.warn(
        '[GeminiOAuthAdapter] GOOGLE_CLOUD_PROJECT is not set — OAuth generateContent may return empty responses',
      );
    }
    return headers;
  }

  /**
   * Generate structured output using Gemini's native JSON response format.
   */
  async structuredOutput(
    options: StructuredOutputOptions<Record<string, unknown>>
  ): Promise<StructuredOutputResult<unknown>> {
    const url = `${this.baseUrl}/${this.model}:generateContent`;
    const chatOpts = options.chatOptions;
    const messages = chatOpts.messages.map((m) => ({
      role: m.role === 'assistant' ? 'model' : 'user',
      parts: toGeminiParts(m.content),
    }));

    const body = {
      contents: messages,
      generationConfig: {
        temperature: chatOpts.temperature ?? 0.7,
        maxOutputTokens: chatOpts.maxTokens ?? 2048,
        topP: chatOpts.topP ?? 0.95,
        responseMimeType: 'application/json',
        responseSchema: options.outputSchema,
      },
      ...(chatOpts.systemPrompts?.length && {
        systemInstruction: { parts: [{ text: chatOpts.systemPrompts.join('\n') }] },
      }),
    };

    const response = await fetch(url, {
      method: 'POST',
      headers: this.buildHeaders(),
      body: JSON.stringify(body),
    });

    if (!response.ok) {
      const errorText = await response.text();
      throw new Error(`Gemini API error: ${response.status} ${errorText}`);
    }

    const data = await response.json();
    const rawText = data.candidates?.[0]?.content?.parts?.[0]?.text || '';

    let parsed: unknown;
    try {
      parsed = JSON.parse(rawText);
    } catch {
      try {
        parsed = JSON.parse(repairTruncatedJson(rawText));
      } catch {
        throw new Error(
          `Failed to parse structured output as JSON. Content: ${rawText.slice(0, 200)}${rawText.length > 200 ? '...' : ''}`
        );
      }
    }

    return { data: parsed, rawText };
  }

  async *chatStream(
    options: TextOptions<Record<string, unknown>>
  ): AsyncIterable<StreamChunk> {
    // Use SSE streaming endpoint for true token-by-token streaming
    const url = `${this.baseUrl}/${this.model}:streamGenerateContent?alt=sse`;
    const messages = options.messages.map((m) => ({
      role: m.role === 'assistant' ? 'model' : 'user',
      parts: toGeminiParts(m.content),
    }));

    const body = {
      contents: messages,
      generationConfig: {
        temperature: options.temperature ?? 0.7,
        maxOutputTokens: options.maxTokens ?? 2048,
        topP: options.topP ?? 0.95,
      },
      ...(options.systemPrompts?.length && {
        systemInstruction: { parts: [{ text: options.systemPrompts.join('\n') }] },
      }),
    };

    const response = await fetch(url, {
      method: 'POST',
      headers: this.buildHeaders(),
      body: JSON.stringify(body),
    });

    if (!response.ok) {
      const errorText = await response.text();
      throw new Error(`Gemini API error: ${response.status} ${errorText}`);
    }

    if (!response.body) {
      throw new Error('Gemini API returned no response body');
    }

    const timestamp = Date.now();
    const runId = `gemini-${timestamp}-${Math.random().toString(36).slice(2, 9)}`;
    const messageId = `msg-${timestamp}-${Math.random().toString(36).slice(2, 9)}`;

    yield { type: EventType.RUN_STARTED, runId, model: this.model, timestamp, threadId: runId };
    yield {
      type: EventType.TEXT_MESSAGE_START,
      messageId,
      model: this.model,
      timestamp,
      role: 'assistant',
    };

    // Parse SSE stream, yielding TEXT_MESSAGE_CONTENT for each text delta as it arrives
    const reader = response.body.getReader();
    const decoder = new TextDecoder();
    let buffer = '';
    let accumulated = '';
    let finalUsage: { promptTokens: number; completionTokens: number; totalTokens: number } | undefined;
    let finishReason: 'stop' | 'length' | 'content_filter' | 'tool_calls' = 'stop';

    try {
      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        buffer += decoder.decode(value, { stream: true });

        // SSE frames are separated by double newlines
        let sepIndex: number;
        while ((sepIndex = buffer.indexOf('\n\n')) !== -1) {
          const frame = buffer.slice(0, sepIndex);
          buffer = buffer.slice(sepIndex + 2);

          // Each frame may have one or more "data: ..." lines
          const dataLines = frame
            .split('\n')
            .filter((l) => l.startsWith('data: '))
            .map((l) => l.slice(6).trim());

          for (const dataLine of dataLines) {
            if (!dataLine || dataLine === '[DONE]') continue;
            let chunk: GeminiSSEChunk;
            try {
              chunk = JSON.parse(dataLine) as GeminiSSEChunk;
            } catch {
              continue; // skip malformed chunk
            }

            const parts = chunk.candidates?.[0]?.content?.parts ?? [];
            for (const part of parts) {
              if (typeof part.text === 'string' && part.text.length > 0) {
                accumulated += part.text;
                yield {
                  type: EventType.TEXT_MESSAGE_CONTENT,
                  messageId,
                  model: this.model,
                  timestamp: Date.now(),
                  delta: part.text,
                  content: accumulated,
                };
              }
            }

            const reason = chunk.candidates?.[0]?.finishReason;
            if (reason) {
              finishReason = mapFinishReason(reason);
            }

            if (chunk.usageMetadata) {
              finalUsage = {
                promptTokens: chunk.usageMetadata.promptTokenCount ?? 0,
                completionTokens: chunk.usageMetadata.candidatesTokenCount ?? 0,
                totalTokens: chunk.usageMetadata.totalTokenCount ?? 0,
              };
            }
          }
        }
      }
    } finally {
      reader.releaseLock();
    }

    yield { type: EventType.TEXT_MESSAGE_END, messageId, model: this.model, timestamp: Date.now() };
    yield {
      type: EventType.RUN_FINISHED,
      runId,
      model: this.model,
      timestamp: Date.now(),
      finishReason,
      usage: finalUsage,
      threadId: runId,
    };
  }
}

// ─── Multimodal helpers ────────────────────────────────────────────────────

interface GeminiPart {
  text?: string;
  inlineData?: { mimeType: string; data: string };
}

interface GeminiSSEChunk {
  candidates?: Array<{
    content?: { parts?: GeminiPart[] };
    finishReason?: string;
  }>;
  usageMetadata?: {
    promptTokenCount?: number;
    candidatesTokenCount?: number;
    totalTokenCount?: number;
  };
}

/**
 * Convert a TanStack AI message content into Gemini parts.
 * Supports plain strings and arrays of `{ type, text }` / `{ type, image, mimeType }` parts.
 */
function toGeminiParts(content: unknown): GeminiPart[] {
  if (typeof content === 'string') return [{ text: content }];
  if (!Array.isArray(content)) return [{ text: '' }];

  const parts: GeminiPart[] = [];
  for (const part of content) {
    if (!part || typeof part !== 'object') continue;
    const p = part as Record<string, unknown>;

    if (typeof p.text === 'string') {
      parts.push({ text: p.text });
      continue;
    }

    // Image part: { type: 'image', image: 'data:image/png;base64,...' | base64String, mimeType?: string }
    if (p.type === 'image' || p.image || p.inlineData) {
      const inline = p.inlineData as { mimeType?: string; data?: string } | undefined;
      if (inline?.data) {
        parts.push({
          inlineData: { mimeType: inline.mimeType ?? 'image/jpeg', data: inline.data },
        });
        continue;
      }

      const raw = (p.image ?? p.data) as string | undefined;
      if (typeof raw === 'string') {
        // Strip data URL prefix if present
        const match = raw.match(/^data:([^;]+);base64,(.*)$/);
        if (match) {
          parts.push({ inlineData: { mimeType: match[1], data: match[2] } });
        } else {
          parts.push({
            inlineData: {
              mimeType: (p.mimeType as string) ?? 'image/jpeg',
              data: raw,
            },
          });
        }
      }
    }
  }

  return parts.length > 0 ? parts : [{ text: '' }];
}

function mapFinishReason(
  reason: string
): 'stop' | 'length' | 'content_filter' | 'tool_calls' {
  switch (reason) {
    case 'STOP':
      return 'stop';
    case 'MAX_TOKENS':
      return 'length';
    case 'SAFETY':
    case 'RECITATION':
      return 'content_filter';
    default:
      return 'stop';
  }
}

/**
 * Create a TanStack AI Gemini text adapter.
 *
 * Authenticated user inference is BYOK-only: OAuth access token, then the
 * caller's own API key. The platform `GEMINI_API_KEY` is never used here.
 */
export async function createGeminiAdapter(
  config: AIConfig = {}
): Promise<AnyTextAdapter> {
  const { geminiText, GeminiTextModels } = await import("@tanstack/ai-gemini");
  const model = resolveModel("gemini", config);

  // Validate model against the known list; fall back to flash if invalid
  const validModel = GeminiTextModels.includes(
    model as (typeof GeminiTextModels)[number]
  )
    ? (model as (typeof GeminiTextModels)[number])
    : GeminiTextModels[0];

  // Priority 1: OAuth Access Token (delegation)
  if (config.userAccessToken) {
    return new GeminiOAuthAdapter(validModel, config.userAccessToken);
  }

  // Priority 2: BYOK (personal API key) — no platform-key fallback
  if (!config.userApiKey) {
    throw new Error(
      "@flowstate/ai: No Gemini credentials. Provide userAccessToken or userApiKey (BYOK). Platform GEMINI_API_KEY is not used for user inference."
    );
  }

  return geminiText(validModel, {
    apiKey: config.userApiKey,
  }) as unknown as AnyTextAdapter;
}

/**
 * Create a TanStack AI OpenRouter text adapter.
 *
 * OpenRouter is a federated gateway exposing 200+ models (Anthropic, Google,
 * Meta, xAI, Mistral, etc.) behind a single OpenAI-compatible API. This makes
 * it ideal for Mycelium principals that need provider flexibility per task
 * (cheap models for batch work, frontier models for reasoning) without
 * juggling multiple SDKs.
 *
 * Credentials precedence:
 *   1. `config.userApiKey` — BYOK from the principal
 *   2. `OPENROUTER_API_KEY` env var — system fallback
 */
export async function createOpenRouterAdapter(
  config: AIConfig = {}
): Promise<AnyTextAdapter> {
  const { createOpenRouterText } = await import("@tanstack/ai-openrouter");
  const model = resolveModel("openrouter", config);

  const apiKey = config.userApiKey || process.env.OPENROUTER_API_KEY;
  if (!apiKey) {
    throw new Error(
      '@flowstate/ai: No OpenRouter credentials. Provide userApiKey or set OPENROUTER_API_KEY environment variable.'
    );
  }

  // OpenRouter's adapter types `model` as a union of known slugs. We accept
  // any string at runtime because the OpenRouter API itself accepts any model
  // slug from its catalogue (300+) and we don't want to gate on stale typings.
  return createOpenRouterText(
    model as Parameters<typeof createOpenRouterText>[0],
    apiKey,
    {
      // Optional but recommended for OpenRouter's app leaderboard / attribution.
      httpReferer: process.env.OPENROUTER_REFERER ?? "https://flowstateautomate.com",
      appTitle: process.env.OPENROUTER_APP_TITLE ?? "FlowState Automate",
    }
  ) as unknown as AnyTextAdapter;
}

/**
 * Resolve the correct TanStack AI server adapter from the given config.
 * Falls back to Gemini flash if no provider is specified.
 *
 * NOTE: This is an async factory so it can do dynamic imports — provider
 * packages are optional peer deps that may not be installed in every app.
 */
export async function resolveAdapter(
  config: AIConfig = {}
): Promise<AnyTextAdapter> {
  const provider = config.provider ?? "gemini";
  switch (provider) {
    case "gemini":
      return createGeminiAdapter(config);
    case "openrouter":
      return createOpenRouterAdapter(config);
    default:
      throw new Error(
        `@flowstate/ai: provider "${provider}" is not yet supported. ` +
          `Install the adapter package and add a case to resolveAdapter().`
      );
  }
}

export { resolveModel };
export type { AIConfig, AIProvider, ModelTier };
